import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Phone, PhoneOff, X, User } from "lucide-react";
import { Button } from "@/components/ui/button";

// DialerListener — mounts once inside the authenticated shell. It registers
// a persistent Twilio Device for inbound calls (no UI required from the user).
// When an inbound call arrives it renders a screen-pop overlay.
// The device is re-registered automatically when the access token expires.

interface ResolvedCaller {
  name: string;
  type: "lead" | "client" | "contact";
  id: string;
}

interface DialerListenerProps {
  dialerEnabled: boolean;
}

export function DialerListener({ dialerEnabled }: DialerListenerProps) {
  const deviceRef = useRef<any>(null);
  const callRef = useRef<any>(null);
  const tokenExpiresRef = useRef<number>(0);
  const reregisterTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [inboundCall, setInboundCall] = useState<{
    from: string;
    resolved: ResolvedCaller | null;
  } | null>(null);
  const [callState, setCallState] = useState<"ringing" | "active" | null>(null);

  const tokenQuery = useQuery<{ token?: string; identity?: string; ttlSeconds?: number; error?: string }>({
    queryKey: ["/api/dialer/token-listener"],
    queryFn: async () => {
      try {
        const res = await apiRequest("POST", "/api/dialer/token", {});
        return res.json();
      } catch (e: any) {
        return { error: e?.message || "Dialer not configured" };
      }
    },
    enabled: dialerEnabled,
    retry: false,
    staleTime: 50 * 60 * 1000, // 50 minutes — token is 60 min
    refetchInterval: 50 * 60 * 1000,
  });

  // Resolve caller from the phone number.
  const resolveCaller = async (phone: string): Promise<ResolvedCaller | null> => {
    if (!phone) return null;
    try {
      const res = await fetch(`/api/dialer/resolve-caller?phone=${encodeURIComponent(phone)}`, {
        credentials: "include",
      });
      if (!res.ok) return null;
      const data = await res.json();
      return data?.name ? data : null;
    } catch {
      return null;
    }
  };

  // Register/re-register the Twilio Device when a token is available.
  useEffect(() => {
    if (!dialerEnabled) return;
    const token = tokenQuery.data?.token;
    if (!token) return;

    let cancelled = false;

    (async () => {
      try {
        const sdk: any = await import("@twilio/voice-sdk");
        if (cancelled) return;
        const Device = sdk?.Device || sdk?.default?.Device;
        if (!Device) return;

        // Destroy any existing device before creating a new one.
        try { deviceRef.current?.destroy?.(); } catch {}

        const device = new Device(token, { codecPreferences: ["opus", "pcmu"] });
        deviceRef.current = device;

        device.on?.("registered", () => {
          console.log("[DialerListener] Device registered for inbound calls");
        });

        device.on?.("incoming", async (call: any) => {
          const from = call?.parameters?.From || call?.callerInfo?.callerName || "";
          callRef.current = call;
          setCallState("ringing");
          const resolved = await resolveCaller(from);
          setInboundCall({ from, resolved });

          call.on?.("disconnect", () => {
            setInboundCall(null);
            setCallState(null);
            callRef.current = null;
          });
          call.on?.("cancel", () => {
            setInboundCall(null);
            setCallState(null);
            callRef.current = null;
          });
          call.on?.("error", () => {
            setInboundCall(null);
            setCallState(null);
            callRef.current = null;
          });
        });

        device.on?.("tokenWillExpire", () => {
          // Force a refetch 2 min before expiry.
          tokenQuery.refetch?.();
        });

        await device.register();
      } catch (e) {
        console.warn("[DialerListener] Failed to register Twilio Device:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [dialerEnabled, tokenQuery.data?.token]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (reregisterTimerRef.current) clearTimeout(reregisterTimerRef.current);
      try { deviceRef.current?.destroy?.(); } catch {}
    };
  }, []);

  const handleAnswer = () => {
    try {
      callRef.current?.accept?.();
      setCallState("active");
    } catch (e) {
      console.warn("[DialerListener] Failed to accept call:", e);
    }
  };

  const handleDecline = () => {
    try {
      callRef.current?.reject?.();
    } catch {}
    setInboundCall(null);
    setCallState(null);
    callRef.current = null;
  };

  const handleHangup = () => {
    try {
      callRef.current?.disconnect?.();
    } catch {}
    setInboundCall(null);
    setCallState(null);
    callRef.current = null;
  };

  if (!inboundCall || !callState) return null;

  const callerLabel = inboundCall.resolved?.name || inboundCall.from || "Unknown caller";
  const callerSub = inboundCall.resolved
    ? `${inboundCall.from}${inboundCall.resolved.type ? ` · ${inboundCall.resolved.type}` : ""}`
    : inboundCall.from;

  return (
    <div
      className="fixed top-16 right-4 z-[200] w-80 bg-card border border-border rounded-xl shadow-2xl p-4 animate-in slide-in-from-top-2 fade-in duration-200"
      data-testid="inbound-call-overlay"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div className="min-w-0">
            <div className="font-semibold text-sm truncate" data-testid="text-inbound-caller-name">
              {callerLabel}
            </div>
            <div className="text-xs text-muted-foreground truncate" data-testid="text-inbound-caller-sub">
              {callState === "ringing" ? "Incoming call" : "In call"} · {callerSub}
            </div>
          </div>
        </div>
        {callState === "active" && (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0"
            onClick={handleHangup}
            data-testid="button-inbound-dismiss"
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {callState === "ringing" ? (
        <div className="flex items-center gap-2">
          <Button
            className="flex-1 bg-green-600 hover:bg-green-700 text-white gap-2"
            onClick={handleAnswer}
            data-testid="button-inbound-answer"
          >
            <Phone className="h-4 w-4" />
            Answer
          </Button>
          <Button
            variant="destructive"
            className="flex-1 gap-2"
            onClick={handleDecline}
            data-testid="button-inbound-decline"
          >
            <PhoneOff className="h-4 w-4" />
            Decline
          </Button>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div className="text-sm text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
            Call in progress
          </div>
          <Button
            variant="destructive"
            size="sm"
            className="gap-1.5"
            onClick={handleHangup}
            data-testid="button-inbound-hangup"
          >
            <PhoneOff className="h-3.5 w-3.5" />
            Hang up
          </Button>
        </div>
      )}
    </div>
  );
}
